import { MaterialCommunityIcons } from "@expo/vector-icons";
import type { MedicationExtractionFailureKind } from "@taskhealth/shared";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    KeyboardAvoidingView,
    Modal,
    Pressable,
    Platform,
    SafeAreaView,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { ActivityIndicator, Snackbar, Text } from "react-native-paper";

import { UploadSourceSheet } from "@/components/compliance/UploadSourceSheet";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DictationQuestionToasts } from "@/components/medications/DictationQuestionToasts";
import { DraftCard, type CardSelections } from "@/components/medications/DraftCard";
import {
    EmptyNoMeds,
    ExtractionError,
    ExtractionZeroResults,
    ProcessingBanner
} from "@/components/medications/EmptyStates";
import { ExtractionProgress } from "@/components/medications/ExtractionProgress";
import { MedicationUploadWizard } from "@/components/medications/MedicationUploadWizard";
import { PatientPhotoImportBanner } from "@/components/medications/PatientPhotoImportBanner";
import { SavedItemCard } from "@/components/medications/SavedItemCard";
import { StickyBottomBar } from "@/components/medications/StickyBottomBar";
import { VoiceCapturePanel } from "@/components/medications/VoiceCapturePanel";
import { UploadStagingModal } from "@/components/onboarding/UploadStagingModal";
import { ageFromDob } from "@/components/questions/bmi-classify";
import { ContextHintBlock } from "@/components/section/ContextHints";
import { ContextItemSheet } from "@/components/visit/ContextItemSheet";
import { useContextVisibility } from "@/components/visit/ContextVisibility";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import type { EnrichmentResult } from "@/hooks/useDraftEnrichment";
import { usePollWhileProcessing } from "@/hooks/usePollWhileProcessing";
import { useUploadFlow } from "@/hooks/useUploadFlow";
import { useVisitContext } from "@/hooks/useVisitContext";
import {
    ApiError,
    fetchMedications,
    fetchDrafts,
    fetchExtractionStatus,
    fetchPatientPhotoMedications,
    importPatientPhotoMedications,
    dismissExtractionJob,
    getUploadUrls,
    extractFromImages,
    deleteDraft,
    promoteDraft,
    completeSection,
    type ContextHint,
    type ContextItemView,
    type Medication,
    type MedicationDraft,
    type MedicationDraftNormalizationSuggestions,
    type MedicationDraftProductSuggestion,
    type MedicationExtractionStatus,
    type PatientPhotoMedications
} from "@/lib/api";
import { useFormContext } from "@/lib/FormContext";
import { checkDuplicate } from "@/lib/medication-utils";
import {
    readWizardShownCount,
    bumpWizardShownCount,
    shouldShowWizard
} from "@/lib/medication-wizard-state";
import { colors, fonts, spacing } from "@/lib/theme";
import { useWebSocketEvent } from "@/lib/websocket";

/**
 * A medication extraction failure the hub is showing.
 *
 * `kind` decides WHICH screen: a job that read the photos and found nothing is a
 * different problem from one whose pipeline broke, and the two need opposite
 * advice ("new photos" vs "same photos again"). `jobId` is what a dismissal
 * names, so the failure can be silenced without silencing the next one.
 */
interface ExtractionFailure {
    kind: MedicationExtractionFailureKind;
    message: string;
    jobId: string | null;
}

/** Fallback wording when the server sent no reason of its own. */
function defaultFailureMessage(kind: MedicationExtractionFailureKind): string {
    return kind === "no_medications"
        ? "No medications could be identified in the photos. Try again with new photos."
        : "Medication extraction failed. Please try again.";
}

type MedicationDraftEventPayload = MedicationDraft & {
    suggestedMedicationOptions?: MedicationDraftProductSuggestion[] | null;
    suggestedDoses?: string[] | null;
    suggestedFrequencies?: string[] | null;
    suggestedAdministrationNotes?: string[] | null;
};

function eventSuggestionsToNormalization(
    draft: MedicationDraftEventPayload
): MedicationDraftNormalizationSuggestions | undefined {
    if (draft.normalizationSuggestions) {
        return draft.normalizationSuggestions;
    }
    const hasSuggestionColumns =
        draft.suggestedMedicationOptions != null ||
        draft.suggestedDoses != null ||
        draft.suggestedFrequencies != null ||
        draft.suggestedAdministrationNotes != null;
    if (!hasSuggestionColumns) {
        return undefined;
    }
    return {
        medicationOptions: draft.suggestedMedicationOptions ?? [],
        doses: draft.suggestedDoses ?? [],
        frequencies: draft.suggestedFrequencies ?? [],
        administrationNotes: draft.suggestedAdministrationNotes ?? []
    };
}

function toMobileDraft(draft: MedicationDraftEventPayload): MedicationDraft {
    return {
        ...draft,
        id: String(draft.id),
        visitId: String(draft.visitId),
        patientId: String(draft.patientId),
        jobId: draft.jobId == null ? null : String(draft.jobId),
        promotedToMedicationId:
            draft.promotedToMedicationId == null ? null : String(draft.promotedToMedicationId),
        medicationName: draft.medicationName ?? draft.name,
        strength: draft.strength ?? draft.dosage,
        orderedDose: draft.orderedDose ?? null,
        sourceImageUrl: draft.sourceImageUrl ?? null,
        normalizationSuggestions: eventSuggestionsToNormalization(draft)
    };
}

function inlineDraftEnrichment(draft: MedicationDraft): EnrichmentResult {
    const draftSuggestions = draft.normalizationSuggestions;
    const suggestedPayload = draftSuggestions
        ? {
              doses: draftSuggestions.doses ?? [],
              frequencies: draftSuggestions.frequencies ?? [],
              administrationNotes: draftSuggestions.administrationNotes ?? [],
              icdCodes: draft.suggestedIcdCodes ?? []
          }
        : null;
    const hasDraftSuggestions = Boolean(
        draftSuggestions &&
        ((draftSuggestions.doses?.length ?? 0) > 0 ||
            (draftSuggestions.frequencies?.length ?? 0) > 0 ||
            (draftSuggestions.administrationNotes?.length ?? 0) > 0 ||
            (draftSuggestions.medicationOptions?.length ?? 0) > 0 ||
            (draft.suggestedIcdCodes?.length ?? 0) > 0)
    );
    const isSupplement = draft.isSupplement ?? false;
    const missingDose = !isSupplement && !draft.orderedDose;
    const missingFrequency = !isSupplement && !draft.frequency;
    const missingDiagnosis = !isSupplement && !draft.suggestedIcdCodes?.length;
    const missingRequired = missingDose || missingFrequency || missingDiagnosis;
    if (draftSuggestions && hasDraftSuggestions) {
        return {
            suggestions: suggestedPayload,
            source: "lookup",
            loading: false,
            failed: missingRequired
        };
    }

    return {
        suggestions: null,
        source: null,
        loading: false,
        failed: missingRequired
    };
}

/**
 * Age at or above which the medications section must carry at least one saved
 * medication before the RN can complete it. Pediatric patients (under 18) are
 * routinely on nothing, so they are exempt from the guard.
 */
const MEDICATION_REQUIRED_MIN_AGE = 18;

export default function MedicationsHubScreen() {
    const { id: visitId, sectionId: sectionIdParam } = useLocalSearchParams<{
        id: string;
        sectionId: string;
    }>();
    const router = useRouter();
    const { form, updateForm } = useFormContext();

    // Defensive: if the caller (Diagnosis Hub, MedicationQuestion, draft-review's
    // router.replace fallback, etc.) didn't pass sectionId in the URL, look up the
    // medications section ourselves from the form definition. Single medications
    // section per visit — unambiguous lookup by behaviorType.
    const sectionId =
        sectionIdParam ??
        form.phases.flatMap((p) => p.sections).find((s) => s.behaviorType === "medications")?.id;

    const contextVisibility = useContextVisibility();
    const { context: patientContext } = useVisitContext(visitId, true, { poll: false });
    const medicationHints = (patientContext?.hints ?? []).filter(
        (hint) => String(hint.sectionId) === String(sectionId)
    );
    const [contextSheet, setContextSheet] = useState<{
        title: string;
        items: ContextItemView[];
        questionId: number;
    } | null>(null);
    const openContextHint = (hint: ContextHint) => {
        if (hint.sourceViewEnabled === false) {
            return;
        }
        const items = (patientContext?.sections ?? [])
            .flatMap((section) => section.items)
            .filter((item) => hint.itemIds.includes(item.id) && item.canOpenSource);
        if (items.length) {
            setHintsVisible(false);
            setContextSheet({ title: hint.sourceLabel, items, questionId: hint.questionId });
        }
    };
    const [hintsVisible, setHintsVisible] = useState(false);

    // Under-18 patients are exempt from the "must have at least one medication"
    // guard below. An unknown/unparseable DOB is deliberately NOT an exemption —
    // we fail closed so the guard still fires rather than silently letting an
    // empty medication section through on missing demographics.
    const patientAgeYears = ageFromDob(form.patient?.dob, new Date());
    const isPediatricPatient =
        patientAgeYears !== null && patientAgeYears < MEDICATION_REQUIRED_MIN_AGE;

    const [medications, setMedications] = useState<Medication[]>([]);
    const [drafts, setDrafts] = useState<MedicationDraft[]>([]);
    // Medications read off the PATIENT's pre-visit photos, waiting for her to
    // import them. Null until the first load, and after a failed one.
    const [patientPhotos, setPatientPhotos] = useState<PatientPhotoMedications | null>(null);
    const [importingPatientPhotos, setImportingPatientPhotos] = useState(false);
    const [loading, setLoading] = useState(true);
    const [extracting, setExtracting] = useState(false);
    const [voiceOpen, setVoiceOpen] = useState(false);
    // Photos and dictation share one extraction pipeline and one `extracting`
    // flag, but they are not the same thing to read about: "Processing your
    // photos…" after dictating is simply wrong. The websocket events carry no
    // source, so this records what THIS screen started; a run picked up on focus
    // that we did not start stays null and gets neutral wording.
    const [extractionSource, setExtractionSource] = useState<"photo" | "voice" | null>(null);
    // WHICH job is running, not just that one is. Photos and dictation can be in
    // flight together, and a photo card whose own job already finished must keep
    // its photo link rather than falling back to a spinner because dictation
    // started afterwards.
    const [extractionJobId, setExtractionJobId] = useState<string | null>(null);
    // Same value, readable from inside the websocket handlers without making
    // them depend on a re-render to see it. The `failed` push may arrive with no
    // id of its own on an older API build, and this is the fallback.
    const extractionJobIdRef = useRef<string | null>(null);
    const listRef = useRef<ScrollView | null>(null);
    // y offset of each draft card, filled in by onLayout, so a new card can be
    // scrolled to without guessing at row heights.
    const cardOffsets = useRef(new Map<string, number>());
    // Draft ids already on screen. Anything not in here when `drafts` changes is
    // new, and new is what we scroll to.
    const seenDraftIds = useRef<Set<string> | null>(null);
    // The failure the hub is currently showing, or null. Held as an object
    // rather than a message string because the two failure kinds are two
    // different screens with two different pieces of advice, and dismissing one
    // has to name the job it dismisses.
    const [extractFailure, setExtractFailure] = useState<ExtractionFailure | null>(null);
    // Set while the dismissal POST is in flight, so the button cannot be tapped
    // twice and the nurse is not navigated away before it lands.
    const [dismissing, setDismissing] = useState(false);
    const [savingDraftId, setSavingDraftId] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<string | null>(null);
    const [wizardVisible, setWizardVisible] = useState(false);
    // "Add via Photo" asks camera-or-library rather than picking for her.
    // It was wired straight to the camera until Jul 2026 and straight to the
    // library after, and each of those silently removed the other: the camera
    // is the only way to shoot a bottle she is holding, the library is the only
    // way to send a whole shelf in one trip.
    const [photoSourceVisible, setPhotoSourceVisible] = useState(false);
    // Real extraction facts for the progress view: how many photos the running
    // job holds, and when the server started it. Both come from the server so
    // they survive navigating away and back mid-read; the upload handoff also
    // seeds them locally so the very first frame is not blank.
    const [extractionPhotoCount, setExtractionPhotoCount] = useState<number | null>(null);
    const [extractionStartedAt, setExtractionStartedAt] = useState<string | null>(null);

    const cardSelectionsRef = useRef<Map<string, CardSelections>>(new Map());
    const contentCountsRef = useRef({ medications: 0, drafts: 0 });
    // Staleness guard: focus refetch + WS handlers + the focus status check can
    // all call loadData concurrently. Each call captures an epoch; only the
    // latest commits setMedications/setDrafts, so out-of-order responses can't
    // clobber the list. (Mirrors POC's loadPocEpochRef.)
    const loadDataEpochRef = useRef(0);
    // Server owns the terminal state: the worker has a 3-minute extraction
    // deadline and a 4-minute stale-job reaper. The mobile stale guard only
    // releases the local spinner if the terminal event/status is missed.
    const PENDING_QUEUE_STALE_MS = 5 * 60 * 1000;
    const EXTRACTION_STALE_MS = 5 * 60 * 1000;

    useEffect(() => {
        contentCountsRef.current = { medications: medications.length, drafts: drafts.length };
    }, [medications.length, drafts.length]);

    useEffect(() => {
        extractionJobIdRef.current = extractionJobId;
    }, [extractionJobId]);

    // A failure only takes over the screen when there is nothing else on it. If
    // even one card came back, she has work to do and a full-screen error would
    // hide it — the partial result is the more useful thing to show.
    const setExtractionFailureIfEmpty = useCallback((failure: ExtractionFailure) => {
        const hasCards =
            contentCountsRef.current.medications > 0 || contentCountsRef.current.drafts > 0;
        setExtractFailure(hasCards ? null : failure);
    }, []);

    // Read a server status row into the failure the hub should show, or null
    // when there is nothing to show. Dismissed jobs return null: she has already
    // seen this one and told us to stop.
    const failureFromStatus = useCallback(
        (s: MedicationExtractionStatus): ExtractionFailure | null => {
            if (s.status !== "failed" || s.dismissed) {
                return null;
            }
            const kind: MedicationExtractionFailureKind =
                s.failureKind === "no_medications" ? "no_medications" : "error";
            return {
                kind,
                message: s.failureReason ?? defaultFailureMessage(kind),
                jobId: s.jobId != null ? String(s.jobId) : null
            };
        },
        []
    );

    const extractionAgeMs = useCallback((status: MedicationExtractionStatus) => {
        return status.createdAt ? Date.now() - new Date(status.createdAt).getTime() : 0;
    }, []);

    const pendingQueueIsStale = useCallback(
        (status: MedicationExtractionStatus) =>
            status.status === "pending" &&
            Boolean(status.createdAt) &&
            extractionAgeMs(status) >= PENDING_QUEUE_STALE_MS,
        [extractionAgeMs, PENDING_QUEUE_STALE_MS]
    );

    const processingIsStale = useCallback(
        (status: MedicationExtractionStatus) =>
            status.status === "processing" &&
            Boolean(status.createdAt) &&
            extractionAgeMs(status) >= EXTRACTION_STALE_MS,
        [extractionAgeMs, EXTRACTION_STALE_MS]
    );

    // API-level start event lets the hub switch into processing immediately
    // when extraction is enqueued, before the first normalized draft exists.
    useWebSocketEvent("medication-extraction:started", (data) => {
        if (String(data.visitId) !== visitId) {
            return;
        }
        setExtracting(true);
        setExtractFailure(null);
        setExtractionJobId(data.attemptId != null ? String(data.attemptId) : null);
    });

    // Listen for extraction completion via WebSocket — single source of truth on the hub
    useWebSocketEvent("medication-extraction:completed", (data) => {
        if (String(data.visitId) !== visitId) {
            return;
        }
        // Extraction done — stop processing state and reconcile streamed drafts.
        setExtracting(false);
        setExtractionSource(null);
        setExtractionJobId(null);
        setExtractFailure(null);
        loadData();
    });

    // Stream each backend-normalized draft onto this same screen as soon as the
    // worker creates it. The completed event still refetches at the end as a
    // reconciliation pass.
    useWebSocketEvent("medication-extraction:draft-created", (data) => {
        if (String(data.visitId) !== visitId) {
            return;
        }
        const incoming = toMobileDraft(data.draft as unknown as MedicationDraftEventPayload);
        // A card read off the PATIENT's pre-visit photo reaches her only via
        // "Import from patient photos". The server no longer streams these,
        // but an older API build might; it must not flip the hub into
        // "reading photos" either way.
        if (incoming.origin === "patient_photo") {
            return;
        }
        setExtracting(true);
        setExtractFailure(null);
        setDrafts((prev) => {
            const index = prev.findIndex((draft) => String(draft.id) === String(incoming.id));
            if (index >= 0) {
                const next = [...prev];
                next[index] = incoming;
                return next;
            }
            return [...prev, incoming];
        });
    });

    // Listen for extraction failure via WebSocket
    useWebSocketEvent("medication-extraction:failed", (data) => {
        if (String(data.visitId) !== visitId) {
            return;
        }
        setExtracting(false);
        const kind: MedicationExtractionFailureKind =
            data.failureKind === "no_medications" ? "no_medications" : "error";
        setExtractionFailureIfEmpty({
            kind,
            message: data.error ?? defaultFailureMessage(kind),
            // Older API builds don't send the id; dismissal then falls back to
            // "this visit's latest job", which is the one we are looking at.
            jobId: data.attemptId != null ? String(data.attemptId) : extractionJobIdRef.current
        });
    });

    const loadData = useCallback(async () => {
        if (!visitId) {
            return;
        }
        const epoch = ++loadDataEpochRef.current;
        try {
            const [meds, draftData, patientPhotoData] = await Promise.all([
                fetchMedications(visitId),
                fetchDrafts(visitId),
                // Best-effort: an older API has no such route, and the hub must
                // not fail to load over it.
                fetchPatientPhotoMedications(visitId).catch(() => null)
            ]);
            // Only the latest call commits — drop stale (out-of-order) responses.
            if (loadDataEpochRef.current !== epoch) {
                return;
            }
            setMedications(meds);
            setDrafts(draftData);
            setPatientPhotos(patientPhotoData);
        } catch (err) {
            console.error("Failed to load medications:", err);
        } finally {
            if (loadDataEpochRef.current === epoch) {
                setLoading(false);
            }
        }
    }, [visitId]);

    // "Import from patient photos": the drafts already exist on the server,
    // hidden; importing flips them into the normal review, tagged.
    const handleImportPatientPhotos = useCallback(async () => {
        if (!visitId || importingPatientPhotos) {
            return;
        }
        setImportingPatientPhotos(true);
        try {
            const result = await importPatientPhotoMedications(visitId);
            setSnackbar(
                result.draftCount > 0
                    ? `${result.draftCount} medication${result.draftCount === 1 ? "" : "s"} imported from the patient's photos`
                    : "Nothing to import yet"
            );
            await loadData();
        } catch (err) {
            setSnackbar(
                err instanceof Error ? err.message : "Could not import the patient's photos"
            );
        } finally {
            setImportingPatientPhotos(false);
        }
    }, [visitId, importingPatientPhotos, loadData]);

    // Re-derive `extracting` from server job status on focus, so the in-progress
    // state (and the Add-via-Photo disable) survives navigating away and back —
    // the WS event is best-effort and lost if the screen unmounted. A
    // `processing` job older than EXTRACTION_STALE_MS is treated as not in-flight
    // (orphaned-job guard). Never turns extracting ON for a terminal/none status.
    const syncExtractingFromServer = useCallback(async () => {
        if (!visitId) {
            return;
        }
        try {
            const s = await fetchExtractionStatus(visitId);
            // Adopt the server's own facts about the running job so the progress
            // view is correct after navigating away and back. photoCount is
            // optional (older API builds omit it) — keep whatever we had.
            if (s.status !== "none") {
                setExtractionStartedAt(s.createdAt);
                const runningJobId = (s as { jobId?: number | string | null }).jobId;
                if (runningJobId != null) {
                    setExtractionJobId(String(runningJobId));
                }
                if (typeof s.photoCount === "number") {
                    setExtractionPhotoCount(s.photoCount);
                }
            }
            const inFlight =
                (s.status === "pending" && !pendingQueueIsStale(s)) ||
                (s.status === "processing" && !processingIsStale(s));
            if (inFlight) {
                setExtracting(true);
                setExtractFailure(null);
            } else if (s.status === "failed") {
                setExtracting(false);
                const failure = failureFromStatus(s);
                if (failure) {
                    setExtractionFailureIfEmpty(failure);
                } else {
                    // Dismissed. She has read this failure and asked for it to
                    // stop; the hub falls through to its normal empty state.
                    setExtractFailure(null);
                }
            } else if (pendingQueueIsStale(s)) {
                setExtracting(false);
                setExtractFailure(null);
            } else if (processingIsStale(s)) {
                setExtracting(false);
                setExtractFailure(null);
            } else {
                // Don't fight an in-progress LOCAL upload (extracting set by the
                // upload handoff); only clear when the server says terminal/none.
                setExtracting(false);
                setExtractFailure(null);
            }
        } catch {
            // Status is best-effort; leave local state as-is on failure.
        }
    }, [visitId, pendingQueueIsStale, processingIsStale, setExtractionFailureIfEmpty]);

    useFocusEffect(
        useCallback(() => {
            loadData();
            syncExtractingFromServer();
        }, [loadData, syncExtractingFromServer])
    );

    // ── Poll-while-extracting fallback (HTTP backstop for the WS push) ──
    // `medication-extraction:completed` is best-effort: a dropped/half-open
    // ("zombie") socket swallows it with no replay, and a foreground RN watching
    // the extraction animation never fires the focus refetch — the exact stuck
    // case from the POC incident (PR #823). While extracting, poll the status
    // endpoint so the screen converges within a few seconds of completion
    // regardless of socket liveness. Timeout matches the focus staleness cutoff.
    // onResult acts ONLY on terminal states — it must NOT clear `extracting` on
    // none/pending/processing, or it would race the local upload→extract handoff
    // before the job row exists. loadData() carries its own epoch guard, and the
    // poller stops itself on unmount (won't fire onResult after stop).
    usePollWhileProcessing<MedicationExtractionStatus>({
        active: extracting && Boolean(visitId),
        intervalMs: 4000,
        timeoutMs: EXTRACTION_STALE_MS,
        poll: () => fetchExtractionStatus(visitId),
        isTerminal: (s) =>
            s.status === "completed" ||
            s.status === "failed" ||
            pendingQueueIsStale(s) ||
            processingIsStale(s),
        onResult: (s) => {
            if (s.status === "pending" || s.status === "processing") {
                // Best-effort WS streams draft-created events, but if the socket
                // misses one the RN should still see cards arrive while the job
                // continues. Reconcile from the DB on each status poll.
                setExtractFailure(null);
                loadData();
            }
            if (s.status === "completed") {
                setExtracting(false);
                setExtractFailure(null);
                loadData();
            } else if (s.status === "failed") {
                setExtracting(false);
                const failure = failureFromStatus(s);
                setExtractFailure(null);
                if (failure) {
                    setExtractionFailureIfEmpty(failure);
                }
            } else if (pendingQueueIsStale(s)) {
                setExtracting(false);
                setExtractFailure(null);
            } else if (processingIsStale(s)) {
                setExtracting(false);
                setExtractFailure(null);
            }
        },
        onTimeout: () => {
            void (async () => {
                try {
                    const s = await fetchExtractionStatus(visitId);
                    if (s.status === "completed" || s.status === "none") {
                        setExtracting(false);
                        setExtractFailure(null);
                        loadData();
                        return;
                    }
                    if (s.status === "failed") {
                        setExtracting(false);
                        const failure = failureFromStatus(s);
                        setExtractFailure(null);
                        if (failure) {
                            setExtractionFailureIfEmpty(failure);
                        }
                        return;
                    }
                    if (pendingQueueIsStale(s)) {
                        setExtracting(false);
                        setExtractFailure(null);
                        return;
                    }
                    if (processingIsStale(s)) {
                        setExtracting(false);
                        setExtractFailure(null);
                    }
                } catch {
                    setExtracting(false);
                    setExtractFailure(null);
                }
            })();
        }
    });

    // Photo upload flow — reuses onboarding's useUploadFlow + UploadStagingModal.
    // The hook now owns the S3 PUT loop and keeps the staging sheet open with a
    // "uploading to cloud / don't leave" view until every image is up; this screen
    // only (1) resolves presigned URLs and (2) enqueues extraction once all
    // images have uploaded. Keeping the sheet open keeps the app foregrounded
    // during the fragile upload window (prod visit 505: an app-suspend killed an
    // in-flight PUT because the sheet had already closed). HARD-FAIL is unchanged:
    // any failed image aborts the batch and surfaces the ExtractionError screen;
    // the staged photos persist on-device for a clean re-upload.
    const getUrls = useCallback(
        (filenames: string[]) => {
            if (!visitId) {
                throw new Error("Missing visit id");
            }
            return getUploadUrls(visitId, filenames);
        },
        [visitId]
    );

    const onAllUploaded = useCallback(
        async (s3Keys: string[]) => {
            if (!visitId) {
                // Never abandon an uploaded batch in silence. The photos are
                // already in S3; dropping out here without a word is what made
                // the 2026-08-11 incident invisible to the RN and to Sentry.
                setExtracting(false);
                setExtractFailure({
                    kind: "error",
                    message: "Something went wrong. Your photos are saved. Please try again.",
                    jobId: null
                });
                return;
            }
            // Hand off to the hub's extraction UI (full ProcessingAnimation on an
            // empty hub, slim ProcessingBanner otherwise). The WS listener + focus
            // refetch flip extracting=false when the job completes / fails.
            setExtracting(true);
            setExtractFailure(null);
            // Seed from the batch we just uploaded so the progress view is honest
            // from the first frame; the server status refines it on the next poll.
            setExtractionSource("photo");
            setExtractionPhotoCount(s3Keys.length);
            setExtractionStartedAt(new Date().toISOString());
            try {
                await extractFromImages(visitId, s3Keys);
                await Promise.all([loadData(), syncExtractingFromServer()]);
            } catch (err) {
                setExtracting(false);
                setExtractFailure({
                    kind: "error",
                    message: err instanceof Error ? err.message : "Extraction failed",
                    jobId: null
                });
            }
        },
        [visitId, loadData, syncExtractingFromServer]
    );

    const onUploadError = useCallback((err: unknown) => {
        // Upload hard-fail (one image failed / network drop). Show the existing
        // "Something Went Wrong" screen; nothing was extracted.
        setExtracting(false);
        setExtractFailure({
            kind: "error",
            message: err instanceof Error ? err.message : "Upload failed. Please try again.",
            jobId: null
        });
    }, []);

    const goBackToVisit = useCallback(() => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.replace(`/visit/${visitId}`);
        }
    }, [router, visitId]);

    /**
     * "Dismiss" — the way past a dead extraction.
     *
     * Clearing the error locally was never enough: the hub rebuilds its failure
     * state from the latest job row on every focus, so the nurse who read the
     * error, went back to the assessment and came in again got the same error
     * screen, for the rest of the visit, with no route to the empty state. The
     * dismissal is stored on the job row, so it survives the unmount.
     *
     * She STAYS on the hub. Dismissing is her saying "I have read this, get it
     * out of my way" — and what is behind it is the ordinary empty state, with
     * the how-it-works clip and Add Manually / Voice / Photo. Sending her back
     * to the assessment would make her walk back in to reach the three things
     * she now wants.
     *
     * If the POST fails (no signal) the local state is still cleared, so the
     * screen gets out of her way either way; the failure then reappears on the
     * next focus, which is the honest outcome — nothing was recorded.
     */
    const handleDismissFailure = useCallback(async () => {
        const jobId = extractFailure?.jobId ?? extractionJobIdRef.current;
        setDismissing(true);
        try {
            if (visitId) {
                await dismissExtractionJob(visitId, jobId);
            }
        } catch (err) {
            console.warn("Failed to dismiss medication extraction failure:", err);
        } finally {
            setDismissing(false);
            setExtractFailure(null);
        }
    }, [visitId, extractFailure]);

    const upload = useUploadFlow({
        rnId: "",
        customUpload: { getUrls, onAllUploaded, onUploadError },
        // Cap longest side at 1024px on-device. v1 production uses 1440 successfully on Pro;
        // we go tighter for Flash streaming to keep worker memory in budget.
        // Was OOMing at 9 originals (~22 MB) on a 512 MB Fargate task.
        maxDimension: 1024,
        gallerySelectionLimit: 0,
        onComplete: () => {
            // Staging modal closes automatically; extraction handed off in onAllUploaded.
        }
    });

    const [completing, setCompleting] = useState(false);
    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();

    // Drafts come from photos or from dictation, and the heading has to describe
    // what is actually in the list. Nothing on the draft records which, so this
    // only claims "photos" when a draft can point at one, and only claims voice
    // when this screen is the thing that started the dictation. Everything else
    // — a mixed list, a photo run where the model named no images, a run picked
    // up on focus — gets the neutral word, which is never wrong.
    const anyDraftHasPhotos = drafts.some((draft) => (draft.sourceImageIndexes?.length ?? 0) > 0);
    const draftsSourceTitle = anyDraftHasPhotos
        ? "Extracted from photos"
        : extractionSource === "voice"
          ? "From what you read out"
          : "Extracted";

    // Bring newly arrived medications into view. Live dictation emits one card
    // per bottle while she is still talking, and a card that lands below the
    // fold may as well not have landed.
    useEffect(() => {
        const ids = drafts.map((draft) => String(draft.id));
        if (seenDraftIds.current === null) {
            seenDraftIds.current = new Set(ids); // first render is not an arrival
            return;
        }
        const seen = seenDraftIds.current;
        const arrived = ids.filter((id) => !seen.has(id));
        seenDraftIds.current = new Set(ids);
        if (arrived.length === 0) {
            return;
        }
        // The layout for a card rendered this tick lands a frame later.
        const timer = setTimeout(() => {
            const y = cardOffsets.current.get(arrived[0]);
            if (y !== undefined) {
                listRef.current?.scrollTo({ y: Math.max(0, y - 12), animated: true });
            }
        }, 120);
        return () => clearTimeout(timer);
    }, [drafts]);

    const handleSelectionChange = useCallback((draftId: string, sel: CardSelections) => {
        cardSelectionsRef.current.set(draftId, sel);
    }, []);

    const removeDraft = useCallback((draftId: string) => {
        cardSelectionsRef.current.delete(draftId);
        setDrafts((prev) => prev.filter((draft) => String(draft.id) !== draftId));
    }, []);

    const handleDiscard = useCallback(
        async (draftId: string) => {
            if (!visitId) {
                return;
            }
            try {
                await deleteDraft(visitId, draftId);
                removeDraft(draftId);
            } catch (err) {
                console.error("Failed to discard draft:", err);
                setSnackbar("Failed to discard. Please try again.");
            }
        },
        [visitId, removeDraft]
    );

    const handleSaveDraft = useCallback(
        async (draftId: string): Promise<boolean> => {
            if (!visitId) {
                return false;
            }
            const sel = cardSelectionsRef.current.get(draftId);
            if (!sel) {
                return false;
            }

            setSavingDraftId(draftId);
            try {
                const existingMeds = await fetchMedications(visitId);
                const dup = checkDuplicate(existingMeds, sel.name, sel.isSupplement);
                if (dup) {
                    showDialog(
                        (sel.isSupplement ? "Supplement" : "Medication") + " Already on Profile",
                        `${sel.name} is already on the patient's profile. You cannot add it again.`,
                        [
                            {
                                text: "Discard Draft",
                                style: "destructive",
                                onPress: () => handleDiscard(draftId)
                            },
                            { text: "Cancel", style: "cancel" }
                        ]
                    );
                    return false;
                }

                await promoteDraft(visitId, draftId, {
                    medicationName: sel.name,
                    route: sel.route ?? undefined,
                    strength: sel.strength ?? undefined,
                    strengthNotApplicable: sel.strengthNotApplicable,
                    orderedDose: sel.dose ?? undefined,
                    frequency: sel.frequency ?? undefined,
                    administrationNotes: sel.adminNotes || undefined,
                    maxDailyDose: sel.maxDailyDose ?? undefined,
                    isSupplement: sel.isSupplement,
                    diagnosisCodes:
                        sel.icdCodes.length > 0
                            ? sel.icdCodes.map((code) => ({
                                  icdCode: code.code,
                                  description: code.description
                              }))
                            : undefined
                });
                setTimeout(() => {
                    removeDraft(draftId);
                    loadData();
                    setSnackbar(`${sel.name} saved to profile`);
                }, 700);
                return true;
            } catch (err) {
                console.error("Failed to save draft:", err);
                setSnackbar("Failed to save. Please try again.");
                return false;
            } finally {
                setSavingDraftId(null);
            }
        },
        [visitId, showDialog, handleDiscard, removeDraft, loadData]
    );

    const doComplete = async () => {
        if (!visitId || !sectionId) {
            router.back();
            return;
        }
        setCompleting(true);
        try {
            await completeSection(visitId, sectionId);
        } catch (e) {
            // A 4xx is the server refusing on a business rule — most of the time
            // the same "no medications on an adult" guard we run below, reaching
            // here only from a stale bundle or a mid-flight delete. Surface it and
            // stay put: marking the section completed locally would show a green
            // check that vanishes on the next refetch. Network/5xx keep the
            // best-effort behaviour — the nurse should not be stranded offline.
            const status = e instanceof ApiError ? e.status : undefined;
            if (status !== undefined && status >= 400 && status < 500) {
                setCompleting(false);
                showDialog(
                    "Section Not Completed",
                    e instanceof Error ? e.message : "Please try again."
                );
                return;
            }
            console.warn("Failed to complete medications section:", e);
        }
        updateForm((prev) => ({
            ...prev,
            sectionStatuses: [
                ...prev.sectionStatuses.filter((s) => String(s.sectionId) !== sectionId),
                {
                    id: sectionId,
                    visitId: visitId,
                    sectionId: sectionId,
                    status: "completed",
                    completedAt: new Date().toISOString()
                }
            ]
        }));
        setCompleting(false);
        router.back();
    };

    // "Add Manually" gate. The wizard exists to catch the nurse in the one
    // moment she is about to type a med list she could have photographed — so it
    // only fires when this visit has NOTHING on it yet.
    //
    // Drafts count as "something". A draft can only exist if she already
    // uploaded photos (extraction is the only thing that creates one), so a
    // pending draft is proof she does not need photo coaching. Note this is the
    // opposite of the Complete Section guard, which ignores drafts because
    // completing deletes them — the two guards ask different questions.
    const handleAddManually = async () => {
        const goToManualEntry = () => router.push(`/visit/${visitId}/medications/search`);

        if (!visitId) {
            goToManualEntry();
            return;
        }

        const shownCount = await readWizardShownCount(visitId);
        if (
            !shouldShowWizard({
                savedCount: medications.length,
                draftCount: drafts.length,
                shownCount
            })
        ) {
            goToManualEntry();
            return;
        }

        await bumpWizardShownCount(visitId);
        setWizardVisible(true);
    };

    const handleCompleteSection = () => {
        // Guard: the section is meaningless with nothing saved to the patient's
        // profile. Drafts do not count — completing the section deletes them, so
        // an RN sitting on drafts only would end up with zero medications.
        if (medications.length === 0 && !isPediatricPatient) {
            showDialog(
                "No Medications Added",
                "Can't submit the medication section without medication. Please upload medications first."
            );
            return;
        }
        if (drafts.length > 0) {
            showDialog(
                "Extracted Cards Pending",
                `You have ${drafts.length} extracted medication${drafts.length === 1 ? "" : "s"} that were not saved to the patient's profile. Completing the section will permanently remove these unsaved cards. Are you sure?`,
                [
                    {
                        text: "Stay Here",
                        style: "cancel"
                    },
                    {
                        text: "Complete Anyway",
                        style: "destructive",
                        onPress: async () => {
                            await Promise.all(
                                drafts.map((draft) =>
                                    deleteDraft(visitId!, String(draft.id)).catch(() => {})
                                )
                            );
                            await doComplete();
                        }
                    }
                ]
            );
        } else {
            doComplete();
        }
    };

    if (loading) {
        return (
            <SafeAreaView style={styles.safe}>
                <View style={styles.center}>
                    <ActivityIndicator size="large" />
                </View>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.safe}>
            {/* The model's clarifying questions, over everything. Absolutely
                positioned and box-none, so the list underneath stays usable. */}
            <DictationQuestionToasts visitId={visitId} />

            {/* Header */}
            <View style={styles.header}>
                <TouchableOpacity
                    style={styles.backRow}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 20 }}
                    activeOpacity={0.6}
                    onPress={goBackToVisit}
                >
                    <MaterialCommunityIcons name="chevron-left" size={22} color="#45D2B0" />
                    <Text style={styles.backText}>Back</Text>
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Medications</Text>
                <View style={styles.countRow}>
                    <View style={styles.countPill}>
                        <Text style={styles.countNumber}>{medications.length}</Text>
                        <Text style={styles.countLabel}>saved</Text>
                    </View>
                    <View style={[styles.countPill, drafts.length > 0 && styles.countPillActive]}>
                        <Text style={styles.countNumber}>{drafts.length}</Text>
                        <Text style={styles.countLabel}>draft</Text>
                    </View>
                    {medicationHints.length > 0 && (
                        <TouchableOpacity
                            style={[styles.countPill, styles.hintsButton]}
                            accessibilityRole="button"
                            accessibilityLabel="View medication hints"
                            onPress={() => {
                                medicationHints.forEach((hint) =>
                                    contextVisibility.show(String(hint.questionId))
                                );
                                setHintsVisible(true);
                            }}
                        >
                            <MaterialCommunityIcons
                                name="information-outline"
                                size={16}
                                color={colors.info600}
                            />
                            <Text style={styles.hintsButtonText}>
                                Hints ({medicationHints.length})
                            </Text>
                        </TouchableOpacity>
                    )}
                    {extracting && (
                        <View style={styles.processingPill}>
                            <ActivityIndicator size="small" color="#0F766E" />
                            <Text style={styles.processingPillText}>
                                {extractionSource === "voice"
                                    ? "reading your list"
                                    : extractionSource === "photo"
                                      ? "reading photos"
                                      : "reading"}
                            </Text>
                        </View>
                    )}
                </View>
            </View>

            {/* Slim top banner — only when hub has existing content. */}
            {extracting && (medications.length > 0 || drafts.length > 0) && (
                <ProcessingBanner source={extractionSource} />
            )}
            {/* Medications read off the patient's own pre-visit photos, waiting to
                be imported. Renders nothing when the patient sent none. */}
            <PatientPhotoImportBanner
                data={patientPhotos}
                importing={importingPatientPhotos}
                onImport={handleImportPatientPhotos}
            />
            {/* Content */}
            {extractFailure && medications.length === 0 && drafts.length === 0 ? (
                extractFailure.kind === "no_medications" ? (
                    // The photos were read and held nothing. Retrying THESE
                    // photos would read the same nothing, so the offer is new
                    // ones — straight to the source picker.
                    <ExtractionZeroResults
                        onTryAgain={() => {
                            setExtractFailure(null);
                            setPhotoSourceVisible(true);
                        }}
                        onDismiss={handleDismissFailure}
                        dismissing={dismissing}
                    />
                ) : (
                    // The pipeline broke. The photos are fine and still on S3,
                    // so the offer is the same photos again.
                    <ExtractionError
                        onRetry={() => {
                            setExtractFailure(null);
                            if (upload.stagedImages.length > 0) {
                                upload.reopenStaging();
                            } else {
                                upload.openCamera();
                            }
                        }}
                        onDismiss={handleDismissFailure}
                        dismissing={dismissing}
                    />
                )
            ) : extracting && medications.length === 0 && drafts.length === 0 ? (
                // Truly empty hub during extraction. Real photo count + real
                // elapsed time, in place of the old decorative activity log.
                <ExtractionProgress
                    photoCount={extractionPhotoCount}
                    startedAt={extractionStartedAt}
                    source={extractionSource}
                />
            ) : medications.length === 0 && drafts.length === 0 && !extracting ? (
                <EmptyNoMeds />
            ) : (
                <KeyboardAvoidingView
                    style={styles.contentWrap}
                    behavior={Platform.OS === "ios" ? "padding" : "height"}
                >
                    <ScrollView
                        ref={listRef}
                        contentContainerStyle={styles.listContent}
                        keyboardShouldPersistTaps="handled"
                        automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
                    >
                        {drafts.length > 0 && (
                            <View style={styles.sectionHeader}>
                                <Text style={styles.sectionTitle}>{draftsSourceTitle}</Text>
                                <Text style={styles.sectionSubtitle}>
                                    {extracting
                                        ? "Cards appear as soon as the worker reads them."
                                        : "Review, edit if needed, then save to the profile."}
                                </Text>
                            </View>
                        )}

                        {drafts.map((draft) => (
                            <View
                                key={draft.id}
                                onLayout={(event) =>
                                    cardOffsets.current.set(
                                        String(draft.id),
                                        event.nativeEvent.layout.y
                                    )
                                }
                            >
                                <DraftCard
                                    draft={draft}
                                    enrichment={inlineDraftEnrichment(draft)}
                                    onSaved={handleSaveDraft}
                                    onDiscard={() => handleDiscard(String(draft.id))}
                                    onSelectionChange={(sel) =>
                                        handleSelectionChange(String(draft.id), sel)
                                    }
                                    saving={savingDraftId === String(draft.id)}
                                    extractionInProgress={extracting}
                                    activeExtractionJobId={extractionJobId}
                                />
                            </View>
                        ))}

                        {medications.length > 0 && (
                            <View
                                style={[
                                    styles.sectionHeader,
                                    drafts.length > 0 && styles.savedHeader
                                ]}
                            >
                                <Text style={styles.sectionTitle}>Saved medications</Text>
                                <Text style={styles.sectionSubtitle}>
                                    Already added to this visit profile.
                                </Text>
                            </View>
                        )}

                        {medications.map((item) => (
                            <SavedItemCard
                                key={item.id}
                                medication={item}
                                visitId={visitId}
                                onPress={() =>
                                    router.push(
                                        `/visit/${visitId}/medications/edit?medicationId=${item.id}`
                                    )
                                }
                            />
                        ))}

                        {medications.length === 0 && drafts.length > 0 && (
                            <View style={styles.emptyListWithDrafts}>
                                <Text style={styles.emptyListText}>
                                    No saved medications yet. Save the extracted cards above when
                                    they look right.
                                </Text>
                            </View>
                        )}
                    </ScrollView>
                </KeyboardAvoidingView>
            )}

            {/* Sticky bottom bar.
                Previously wrapped in `drafts.length === 0 &&`, which hid Add
                Manually, Add via Photo AND Complete Section the moment a single
                draft existed. On a visit whose extracted cards could not be
                saved (no suggested ICD code => Save stays disabled) that left
                the nurse with no way forward at all except discarding every
                card one at a time. The bar now always renders; resolving drafts
                before completing is enforced by the "Extracted Cards Pending"
                confirmation in handleCompleteSection, which is the check that
                was meant to do this job. */}
            {voiceOpen ? (
                <VoiceCapturePanel
                    visitId={visitId}
                    knownMedicationNames={medications.map((m) => m.medicationName).filter(Boolean)}
                    onBack={() => setVoiceOpen(false)}
                    onCaptured={() => {
                        setVoiceOpen(false);
                        setExtractionSource("voice");
                        // The count is left over from her last upload; this run
                        // has no photos in it.
                        setExtractionPhotoCount(null);
                        setExtractionStartedAt(new Date().toISOString());
                        setExtracting(true);
                        void loadData();
                    }}
                />
            ) : (
                <StickyBottomBar
                    onAddManually={handleAddManually}
                    onAddViaVoice={() => setVoiceOpen(true)}
                    onAddViaPhoto={() => setPhotoSourceVisible(true)}
                    onCompleteSection={handleCompleteSection}
                    completeSectionLoading={completing}
                    addViaPhotoDisabled={extracting}
                />
            )}

            <UploadSourceSheet
                visible={photoSourceVisible}
                title="Add medications from a photo"
                sources={["camera", "photo"]}
                subtitles={{ photo: "Pick one or more images from your library" }}
                onClose={() => setPhotoSourceVisible(false)}
                onPick={(source) => {
                    if (source === "camera") {
                        upload.openCamera();
                    } else {
                        upload.openGallery();
                    }
                }}
            />

            <MedicationUploadWizard
                visible={wizardVisible}
                onOpenCamera={() => {
                    setWizardVisible(false);
                    upload.openCamera();
                }}
                onSkip={() => {
                    setWizardVisible(false);
                    router.push(`/visit/${visitId}/medications/search`);
                }}
                onDismiss={() => setWizardVisible(false)}
            />

            {/* Photo staging modal — reused from onboarding */}
            <UploadStagingModal
                visible={upload.showStaging}
                stagedImages={upload.stagedImages}
                onAddMoreCamera={upload.openCamera}
                onAddMoreGallery={upload.openGallery}
                onRemove={upload.removeStaged}
                onUpload={upload.uploadAll}
                onCancel={upload.cancelStaging}
                uploading={upload.uploading}
                uploadProgress={upload.uploadProgress}
                uploadFraction={upload.uploadFraction}
                uploadingView="cloud"
            />

            {/* ProcessingOverlay removed — the hub owns full animation + banner states. */}

            <Snackbar
                visible={snackbar !== null}
                onDismiss={() => setSnackbar(null)}
                duration={2500}
                style={styles.snackbar}
            >
                {snackbar ?? ""}
            </Snackbar>

            <Modal
                visible={hintsVisible}
                transparent
                animationType="slide"
                onRequestClose={() => setHintsVisible(false)}
            >
                <Pressable
                    style={styles.hintsBackdrop}
                    onPress={() => setHintsVisible(false)}
                    accessibilityLabel="Close medication hints"
                />
                <View style={styles.hintsSheet}>
                    <View style={styles.hintsHeader}>
                        <Text style={styles.sectionTitle}>Medication hints</Text>
                        <TouchableOpacity
                            onPress={() => setHintsVisible(false)}
                            accessibilityRole="button"
                            accessibilityLabel="Close hints"
                            hitSlop={12}
                        >
                            <MaterialCommunityIcons
                                name="close"
                                size={22}
                                color={colors.textSecondary}
                            />
                        </TouchableOpacity>
                    </View>
                    <ScrollView contentContainerStyle={styles.hintsContent}>
                        <ContextHintBlock hints={medicationHints} onOpen={openContextHint} />
                    </ScrollView>
                </View>
            </Modal>
            <ContextItemSheet
                visitId={visitId}
                items={contextSheet?.items ?? null}
                title={contextSheet?.title ?? null}
                questionId={contextSheet?.questionId}
                onClose={() => setContextSheet(null)}
            />
            <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    hintsButton: { backgroundColor: "#DBEEFF", borderColor: "#BFDBFE" },
    hintsButtonText: { fontFamily: fonts.semiBold, fontSize: 12, color: colors.info600 },
    hintsBackdrop: { flex: 1, backgroundColor: "rgba(0,0,0,0.35)" },
    hintsSheet: {
        maxHeight: "70%",
        backgroundColor: colors.surface,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        padding: spacing.lg
    },
    hintsHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
    hintsContent: { paddingBottom: spacing.lg },
    safe: {
        flex: 1,
        backgroundColor: "#FAFAF9"
    },
    center: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center"
    },
    header: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.sm,
        paddingBottom: spacing.base,
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    backRow: {
        flexDirection: "row",
        alignItems: "center",
        marginLeft: -spacing.xs,
        marginBottom: spacing.sm
    },
    backText: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: colors.teal500
    },
    headerTitle: {
        fontSize: 22,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    countRow: {
        marginTop: 10,
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8
    },
    countPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 9999,
        backgroundColor: "#F8FAFC",
        borderWidth: 1,
        borderColor: "#E2E8F0"
    },
    countPillActive: {
        backgroundColor: "#ECFDF5",
        borderColor: "#99F6E4"
    },
    countNumber: {
        fontSize: 13,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    countLabel: {
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.textSecondary
    },
    processingPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 9999,
        backgroundColor: "#CCFBF1"
    },
    processingPillText: {
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: "#0F766E"
    },
    extractingBanner: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        backgroundColor: "#F5F3FF",
        borderWidth: 1,
        borderColor: "#C4B5FD",
        borderRadius: 10,
        padding: 12,
        marginHorizontal: 20,
        marginTop: 12
    },
    extractingText: {
        fontSize: 12,
        color: "#7C3AED",
        flex: 1
    },
    errorBanner: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "#FEF2F2",
        borderWidth: 1,
        borderColor: "#FCA5A5",
        borderRadius: 10,
        padding: 12,
        marginHorizontal: 20,
        marginTop: 12
    },
    errorText: {
        fontSize: 12,
        color: "#DC2626",
        flex: 1
    },
    errorDismiss: {
        fontSize: 12,
        fontWeight: "600",
        color: "#DC2626",
        marginLeft: 10
    },
    inlineErrorBanner: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
        marginHorizontal: spacing.lg,
        marginTop: spacing.sm,
        padding: 12,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "#FECACA",
        backgroundColor: "#FEF2F2"
    },
    inlineErrorIcon: {
        width: 24,
        height: 24,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#FEE2E2"
    },
    inlineErrorCopy: {
        flex: 1
    },
    inlineErrorTitle: {
        fontSize: 12,
        fontFamily: fonts.extraBold,
        color: "#991B1B"
    },
    inlineErrorText: {
        marginTop: 2,
        fontSize: 11,
        fontFamily: fonts.regular,
        color: "#7F1D1D",
        lineHeight: 15
    },
    contentWrap: {
        flex: 1
    },
    listContent: {
        padding: 16,
        paddingHorizontal: 20,
        paddingBottom: 180
    },
    sectionHeader: {
        marginBottom: 12
    },
    savedHeader: {
        marginTop: 10
    },
    sectionTitle: {
        fontSize: 16,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    sectionSubtitle: {
        marginTop: 3,
        fontSize: 12,
        fontFamily: fonts.regular,
        color: colors.textSecondary,
        lineHeight: 17
    },
    emptyState: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 30
    },
    emptyIcon: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: "#F5F3FF",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 20
    },
    emptyIconText: {
        fontSize: 36
    },
    emptyTitle: {
        fontSize: 17,
        fontWeight: "700",
        color: "#1F1547",
        marginBottom: 8
    },
    emptySubtitle: {
        fontSize: 13,
        color: "#B0AECE",
        textAlign: "center",
        lineHeight: 20
    },
    emptyListWithDrafts: {
        padding: 20,
        alignItems: "center"
    },
    emptyListText: {
        fontSize: 13,
        color: "#B0AECE",
        textAlign: "center"
    },
    snackbar: {
        backgroundColor: colors.textPrimary
    }
});
